<template>
  <div class="max-w-9xl mx-auto p-6">
    <h1 class="text-3xl font-bold text-gray-800 mb-8">
      Department Postal Code Assignments
    </h1>

    <div>
      <!-- Filters Card -->
      <div
        class="mb-8 bg-white shadow-sm border border-gray-200 rounded-xl p-6"
      >
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-lg font-semibold text-gray-900">Filters & Search</h3>
          <div class="flex items-center space-x-2">
            <span class="text-sm text-gray-500">
              <i class="fas fa-filter mr-1"></i>
              Filter assignments
            </span>
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <!-- Department Name Filter -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Department Name
            </label>
            <div class="relative">
              <input
                v-model="filters.departmentName"
                type="text"
                placeholder="Filter by department name..."
                class="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
              <i
                class="fas fa-building absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
              ></i>
            </div>
          </div>

          <!-- Department Code Filter -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Department Code
            </label>
            <div class="relative">
              <input
                v-model="filters.departmentCode"
                type="text"
                placeholder="Filter by department code..."
                class="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
              <i
                class="fas fa-code absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
              ></i>
            </div>
          </div>

          <!-- Country Code Filter -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Country Code
            </label>
            <input
              v-model="filters.countryCode"
              type="text"
              placeholder="Filter by country code..."
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <!-- Postal Code Filter -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Postal Code
            </label>
            <input
              v-model="filters.postalCode"
              type="text"
              placeholder="Filter by postal code..."
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mt-4 flex space-x-3">
          <button
            @click="clearFilters"
            class="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Clear All Filters
          </button>
          <button
            @click="openCreateModal"
            class="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Add New Assignment
          </button>
        </div>

        <!-- Results Count -->
        <div
          v-if="locationDepartments?.data"
          class="mt-4 text-sm text-gray-600"
        >
          Showing {{ filteredAndSortedLocationDepartments.length }} of
          {{ locationDepartments.data.length }} departments
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center py-8">
        <div
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"
        ></div>
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
      >
        <strong>Error:</strong> {{ error }}
      </div>

      <!-- Location Departments Table -->
      <div
        v-else-if="filteredAndSortedLocationDepartments.length > 0"
        class="bg-white shadow-md rounded-lg overflow-hidden"
      >
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th
                @click="sort('departamentoNombre')"
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div class="flex items-center">
                  Department Name
                  <span v-if="sortBy === 'departamentoNombre'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                @click="sort('departamentoCodigo')"
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div class="flex items-center">
                  Department Code
                  <span v-if="sortBy === 'departamentoCodigo'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                @click="sort('countryCode')"
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div class="flex items-center">
                  Country Code
                  <span v-if="sortBy === 'countryCode'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                @click="sort('postalCode')"
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div class="flex items-center">
                  Postal Code
                  <span v-if="sortBy === 'postalCode'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>

              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="(department,
              index) in filteredAndSortedLocationDepartments"
              :key="department.id"
              :class="{ 'bg-gray-50': index % 2 === 1 }"
            >
              <td
                class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
              >
                {{ department.departamentoNombre || "N/A" }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ department.departamentoCodigo || "N/A" }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ department.countryCode || "N/A" }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <span
                  :class="{
                    'text-orange-600 font-semibold':
                      department.postalCode === '*'
                  }"
                >
                  {{ department.postalCode || "N/A" }}
                </span>
                <span
                  v-if="department.postalCode === '*'"
                  class="ml-2 text-xs text-gray-500"
                >
                  (Fallback)
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button
                  @click="editLocationDepartment(department)"
                  class="text-blue-600 hover:text-blue-900 mr-4"
                >
                  Edit
                </button>
                <button
                  @click="deleteLocationDepartment(department)"
                  class="text-red-600 hover:text-red-900"
                >
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- No Location Departments or No Filtered Results -->
      <div v-else-if="!loading" class="text-center py-8 text-gray-500">
        <div v-if="!locationDepartments?.data?.length">
          No location departments found.
        </div>
        <div v-else-if="filteredAndSortedLocationDepartments.length === 0">
          <p>No location departments match the current filters.</p>
          <button
            @click="clearFilters"
            class="mt-2 px-4 py-2 text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Clear all filters
          </button>
        </div>
      </div>
    </div>

    <!-- Create/Edit Assignment Modal -->
    <div
      v-if="showEditModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
      @click="closeEditModal"
    >
      <div
        class="relative top-20 mx-auto p-6 border w-96 shadow-lg rounded-md bg-white"
        @click.stop
      >
        <div class="mt-3">
          <h3 class="text-lg font-medium text-gray-900 mb-4">
            {{ isEditing ? "Edit Assignment" : "Create New Assignment" }}
          </h3>

          <div class="space-y-4">
            <!-- Department Selection -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Department *
              </label>
              <select
                v-model="editForm.departamento_id"
                :disabled="saving || isEditing"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                required
              >
                <option value="">Select a department...</option>
                <option
                  v-for="dept in getDepartments"
                  :key="dept.full.departamentoId"
                  :value="dept.full.departamentoId"
                >
                  {{ dept.code }} - {{ dept.name }}
                </option>
              </select>
              <p v-if="isEditing" class="text-xs text-gray-500 mt-1">
                Department cannot be changed when editing
              </p>
            </div>

            <!-- Country Code -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Country Code *
              </label>
              <input
                v-model="editForm.country_code"
                type="text"
                :disabled="saving"
                placeholder="e.g. US, CA, MX"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                required
                maxlength="2"
              />
            </div>

            <!-- Postal Code -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Postal Code *
              </label>
              <input
                v-model="editForm.postal_code"
                type="text"
                :disabled="saving"
                placeholder="e.g. 12345, *, V1A2B3"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                required
              />
              <p class="text-xs text-gray-500 mt-1">
                Use "*" for fallback assignments
              </p>
            </div>
          </div>

          <!-- Modal Actions -->
          <div class="flex justify-end space-x-3 mt-6">
            <button
              @click="closeEditModal"
              :disabled="saving"
              class="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              @click="saveAssignment"
              :disabled="saving || !isFormValid"
              class="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              <span v-if="saving">{{
                isEditing ? "Updating..." : "Creating..."
              }}</span>
              <span v-else>{{
                isEditing ? "Update Assignment" : "Create Assignment"
              }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  getLocations,
  createLocationDepartment,
  updateLocationDepartment,
  deleteLocationDepartment
} from "@/api/store/LocationDepartmentsPriority";

export default {
  name: "DepartmentPriority",
  data() {
    return {
      locationDepartments: null,
      loading: false,
      error: null,
      filters: {
        departmentName: "",
        departmentCode: "",
        countryCode: "",
        postalCode: ""
      },
      sortBy: "",
      sortOrder: "asc", // 'asc' or 'desc'
      showEditModal: false,
      editingLocationDepartment: null,
      isEditing: false,
      editForm: {
        departamento_id: "",
        country_code: "",
        postal_code: ""
      },
      saving: false
    };
  },
  computed: {
    ...mapGetters("general", ["getDepartments"]),
    filteredAndSortedLocationDepartments() {
      if (!this.locationDepartments?.data) return [];

      let filtered = this.locationDepartments.data.filter(department => {
        // Filter by department name
        if (
          this.filters.departmentName &&
          !department.departamentoNombre
            ?.toLowerCase()
            .includes(this.filters.departmentName.toLowerCase())
        ) {
          return false;
        }

        // Filter by department code
        if (
          this.filters.departmentCode &&
          !department.departamentoCodigo
            ?.toLowerCase()
            .includes(this.filters.departmentCode.toLowerCase())
        ) {
          return false;
        }

        // Filter by country code
        if (
          this.filters.countryCode &&
          !department.countryCode
            ?.toLowerCase()
            .includes(this.filters.countryCode.toLowerCase())
        ) {
          return false;
        }

        // Filter by postal code
        if (
          this.filters.postalCode &&
          !department.postalCode
            ?.toLowerCase()
            .includes(this.filters.postalCode.toLowerCase())
        ) {
          return false;
        }

        return true;
      });

      // Sort if sortBy is set
      if (this.sortBy) {
        filtered.sort((a, b) => {
          let aValue = a[this.sortBy];
          let bValue = b[this.sortBy];

          // Handle null/undefined values
          if (aValue == null) aValue = "";
          if (bValue == null) bValue = "";

          // Convert to string for consistent comparison
          aValue = String(aValue).toLowerCase();
          bValue = String(bValue).toLowerCase();

          if (this.sortOrder === "asc") {
            return aValue.localeCompare(bValue);
          } else {
            return bValue.localeCompare(aValue);
          }
        });
      }

      return filtered;
    },

    isFormValid() {
      return (
        this.editForm.departamento_id &&
        this.editForm.country_code &&
        this.editForm.postal_code
      );
    }
  },
  mounted() {
    this.loadLocationDepartments();
  },
  methods: {
    async loadLocationDepartments() {
      this.loading = true;
      this.error = null;
      try {
        this.locationDepartments = await getLocations();

        // Response is already camelized by httpClient
        console.log("Location Departments:", this.locationDepartments);
      } catch (error) {
        this.error = error.message || "Failed to load location departments";
        console.error("Error fetching location departments:", error);
      } finally {
        this.loading = false;
      }
    },

    editLocationDepartment(locationDept) {
      this.isEditing = true;
      this.editingLocationDepartment = locationDept;
      this.editForm = {
        departamento_id: locationDept.departamentoId || "",
        country_code: locationDept.countryCode || "",
        postal_code: locationDept.postalCode || ""
      };
      this.showEditModal = true;
    },

    sort(field) {
      if (this.sortBy === field) {
        // Toggle sort order if clicking the same field
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        // Set new field and default to ascending
        this.sortBy = field;
        this.sortOrder = "asc";
      }
    },

    clearFilters() {
      this.filters = {
        departmentName: "",
        departmentCode: "",
        countryCode: "",
        postalCode: ""
      };
      this.sortBy = "";
      this.sortOrder = "asc";
    },

    openCreateModal() {
      this.isEditing = false;
      this.editingLocationDepartment = null;
      this.editForm = {
        departamento_id: "",
        country_code: "",
        postal_code: ""
      };
      this.showEditModal = true;
    },

    async deleteLocationDepartment(locationDept) {
      if (
        confirm(
          `Are you sure you want to delete the assignment for ${locationDept.departamentoNombre} - ${locationDept.postalCode}?`
        )
      ) {
        try {
          await deleteLocationDepartment(locationDept.id);

          // Remove from local data
          const index = this.locationDepartments.data.findIndex(
            dept => dept.id === locationDept.id
          );
          if (index !== -1) {
            this.locationDepartments.data.splice(index, 1);
            this.$toast.success("Assignment deleted successfully");
          }
        } catch (error) {
          console.error("Error deleting assignment:", error);
          this.$toast.error("Failed to delete assignment");
        }
      }
    },

    closeEditModal() {
      this.showEditModal = false;
      this.editingLocationDepartment = null;
      this.isEditing = false;
      this.editForm = {
        departamento_id: "",
        country_code: "",
        postal_code: ""
      };
      this.saving = false;
    },

    async saveAssignment() {
      if (!this.isFormValid) return;

      this.saving = true;
      try {
        if (this.isEditing) {
          // Update existing assignment
          const response = await updateLocationDepartment(
            this.editingLocationDepartment.id,
            {
              country_code: this.editForm.country_code,
              postal_code: this.editForm.postal_code,
              departamento_id: this.editForm.departamento_id
            }
          );

          // Update local data with the response data
          const index = this.locationDepartments.data.findIndex(
            dept => dept.id === this.editingLocationDepartment.id
          );

          if (index !== -1) {
            // Map response data to table structure (response is already camelized by httpClient)
            const updatedData = {
              ...this.locationDepartments.data[index],
              id: response.data.id,
              countryCode: response.data.countryCode,
              postalCode: response.data.postalCode,
              departamentoId: response.data.departamentoId,
              departamentoNombre: response.data.departamentoNombre,
              departamentoCodigo: response.data.departamentoCodigo
            };

            this.$set(this.locationDepartments.data, index, updatedData);
          }

          this.$toast.success("Assignment updated successfully");
        } else {
          // Create new assignment
          const response = await createLocationDepartment({
            departamento_id: this.editForm.departamento_id,
            country_code: this.editForm.country_code,
            postal_code: this.editForm.postal_code
          });

          // Add to local data
          const selectedDept = this.getDepartments.find(
            d => d.id == this.editForm.departamento_id
          );

          // Map response data to table structure (response is already camelized by httpClient)
          const newAssignment = {
            id: response.data.id,
            countryCode: response.data.countryCode,
            postalCode: response.data.postalCode,
            departamentoId: response.data.departamentoId,
            departamentoNombre:
              response.data.departamentoNombre ||
              selectedDept?.nombre ||
              "Unknown",
            departamentoCodigo:
              response.data.departamentoCodigo ||
              selectedDept?.codigo ||
              "Unknown"
          };

          this.locationDepartments.data.push(newAssignment);
          this.$toast.success("Assignment created successfully");
        }

        this.closeEditModal();
      } catch (error) {
        console.error("Error saving assignment:", error);
        this.$toast.error(
          `Failed to ${this.isEditing ? "update" : "create"} assignment`
        );
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
